import Layer from '@arcgis/core/layers/Layer';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import { ensureLoadedLayerArray, LayerLoader } from './LayerLoader';

export const TRAFFIC_LAYER_URL =
  'https://traffic.arcgis.com/arcgis/rest/services/World/Traffic/MapServer';

/**
 * The Traffic layer contains traffic for the whole world; we reduce UI
 * complexity by loading the north american sublayers specifically.
 *
 * These are magic numbers, but they're explicitly set on GIS' side to be used
 * for identification in this way -- a layer's id won't change incidentally.
 */
export const NA_TRAFFIC_LAYERS = [
  { id: 4, title: 'Traffic Incidents Detailed' },
  { id: 3, title: 'Traffic Incidents Intermediate' },
  { id: 2, title: 'Traffic Incidents Overview' },

  { id: 51, title: 'Road Closures Detailed' },
  { id: 50, title: 'Road Closures Intermediate' },
  { id: 49, title: 'Road Closures Overview' },

  { id: 6, title: 'Live Traffic' },

  // These layers are temporal layers; it may make more sense not to include
  // them if we're keeping the TimeSlider disabled, but they were included in
  // the past without a slider, so:
  { id: 5, title: 'Traffic' },
  { id: 7, title: 'North America Traffic' },
];

export const northAmericanTrafficLayerLoader: LayerLoader = {
  shouldUseLoader(service) {
    return service.gis_service_url === TRAFFIC_LAYER_URL;
  },
  async load(service) {
    const apiKey = service.token;
    const url = service.gis_service_url;
    const requestUrl = apiKey ? url + `?token=${apiKey}` : url;
    const trafficLayer = (await Layer.fromArcGISServerUrl({
      url: requestUrl,
      properties: {
        apiKey,
        id: service.gisLayerID,
        sublayers: NA_TRAFFIC_LAYERS.map(l => ({ ...l, visible: true })),
        visible: service.auto_checked || false,
        outFields: ['*'],
      },
    })) as MapImageLayer;
    await trafficLayer.load();

    // HAX: Disable the TimeSlider for this layer; our Slider implementation
    // can't handle live data in one sublayer and historic data in another, and
    // the live data is more important for our users. (In the past, the
    // TimeSlider failed to register for the Traffic layer accidentally. This
    // hack honors that existing condition to minimize user-facing changes of
    // this refactor.)
    trafficLayer.timeInfo = undefined as unknown as __esri.TimeInfo;

    return ensureLoadedLayerArray(trafficLayer);
  },
};
